const carriersRow1 = [
  { name: 'Mutual of Omaha', icon: 'ri-shield-star-line', color: '#1A3A52' },
  { name: 'Prudential', icon: 'ri-rock-line', color: '#C26638' },
  { name: 'AIG / Corebridge', icon: 'ri-building-4-line', color: '#1A3A52' },
  { name: 'Nationwide', icon: 'ri-home-heart-line', color: '#C26638' },
  { name: 'North American', icon: 'ri-compass-discover-line', color: '#1A3A52' },
  { name: 'Transamerica', icon: 'ri-global-line', color: '#C26638' },
  { name: 'Banner Life', icon: 'ri-flag-line', color: '#1A3A52' },
  { name: 'Protective Life', icon: 'ri-shield-check-line', color: '#C26638' },
];

const carriersRow2 = [
  { name: 'Lincoln Financial', icon: 'ri-funds-box-line', color: '#C26638' },
  { name: 'American Equity', icon: 'ri-line-chart-line', color: '#1A3A52' },
  { name: 'Pacific Life', icon: 'ri-sailboat-line', color: '#C26638' },
  { name: 'Legal & General', icon: 'ri-scales-3-line', color: '#1A3A52' },
  { name: 'Foresters Financial', icon: 'ri-plant-line', color: '#C26638' },
  { name: 'Global Atlantic', icon: 'ri-earth-line', color: '#1A3A52' },
  { name: 'Americo', icon: 'ri-star-smile-line', color: '#C26638' },
  { name: 'F&G Annuities', icon: 'ri-safe-2-line', color: '#1A3A52' },
];

interface CarrierBadgeProps {
  name: string;
  icon: string;
  color: string;
}

function CarrierBadge({ name, icon, color }: CarrierBadgeProps) {
  return (
    <div
      className="flex items-center gap-2.5 px-4 py-2.5 bg-white rounded-xl border border-gray-100 shrink-0 select-none"
      style={{ minWidth: 'max-content' }}
    >
      <div className="w-7 h-7 flex items-center justify-center rounded-lg shrink-0" style={{ backgroundColor: `${color}18` }}>
        <i className={`${icon} text-[15px]`} style={{ color }}></i>
      </div>
      <span className="text-[13px] font-semibold text-gray-700 whitespace-nowrap">{name}</span>
      <div className="w-4 h-4 flex items-center justify-center shrink-0">
        <i className="ri-checkbox-circle-fill text-[14px] text-emerald-500"></i>
      </div>
    </div>
  );
}

export default function CarriersStrip() {
  return (
    <section className="py-12 md:py-16 bg-white border-b border-gray-100 overflow-hidden">
      <style>{`
        @keyframes marquee-left {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes marquee-right {
          0%   { transform: translateX(-50%); }
          100% { transform: translateX(0); }
        }
        .marquee-left  { animation: marquee-left  30s linear infinite; }
        .marquee-right { animation: marquee-right 30s linear infinite; }
        .marquee-left:hover,
        .marquee-right:hover { animation-play-state: paused; }
      `}</style>

      <div className="max-w-[1200px] mx-auto px-4 sm:px-8 mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#D97642]/10 text-[#C26638] text-[11px] font-bold uppercase tracking-widest rounded-full border border-[#D97642]/20 mb-3">
              <i className="ri-exchange-line text-[12px]"></i>
              Independent = More Options
            </span>
            <h2 className="text-[22px] md:text-[26px] font-bold text-[#1A3A52] leading-tight">
              20+ A-Rated Carriers. One Local Agent. Zero Extra Cost.
            </h2>
            <p className="text-[14px] text-gray-500 mt-1.5 leading-relaxed max-w-[520px]">
              We&apos;re not tied to any single insurance company. We shop the market so you don&apos;t have to — comparing rates and policy features to find your best fit.
            </p>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <div className="text-center">
              <div className="text-[28px] font-extrabold text-[#D97642] leading-none">20+</div>
              <div className="text-[11px] text-gray-400 font-medium uppercase tracking-wider mt-0.5">Carriers</div>
            </div>
            <div className="w-px h-10 bg-gray-200"></div>
            <div className="text-center">
              <div className="text-[28px] font-extrabold text-[#1A3A52] leading-none">A+</div>
              <div className="text-[11px] text-gray-400 font-medium uppercase tracking-wider mt-0.5">Avg Rating</div>
            </div>
            <div className="w-px h-10 bg-gray-200"></div>
            <div className="text-center">
              <div className="text-[28px] font-extrabold text-[#D97642] leading-none">$0</div>
              <div className="text-[11px] text-gray-400 font-medium uppercase tracking-wider mt-0.5">Broker Fee</div>
            </div>
          </div>
        </div>
      </div>

      {/* Row 1 — scrolls left */}
      <div className="relative mb-3">
        <div className="flex gap-3 marquee-left" style={{ width: 'max-content' }}>
          {[...carriersRow1, ...carriersRow1].map((c, i) => (
            <CarrierBadge key={`r1-${i}`} {...c} />
          ))}
        </div>
        {/* Fade edges */}
        <div className="absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-white to-transparent pointer-events-none z-10"></div>
        <div className="absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-white to-transparent pointer-events-none z-10"></div>
      </div>

      {/* Row 2 — scrolls right */}
      <div className="relative">
        <div className="flex gap-3 marquee-right" style={{ width: 'max-content' }}>
          {[...carriersRow2, ...carriersRow2].map((c, i) => (
            <CarrierBadge key={`r2-${i}`} {...c} />
          ))}
        </div>
        <div className="absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-white to-transparent pointer-events-none z-10"></div>
        <div className="absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-white to-transparent pointer-events-none z-10"></div>
      </div>

      {/* Bottom note */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-8 mt-7">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-gray-100">
          <p className="text-[12px] text-gray-400 text-center sm:text-left">
            Carrier availability varies by state and product type. All carriers listed are A-rated or better by AM Best.
          </p>
          <a
            href="/compare"
            className="inline-flex items-center gap-1.5 text-[13px] text-[#D97642] font-semibold hover:text-[#C26638] transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-bar-chart-grouped-line text-[14px]"></i>
            Compare products side by side →
          </a>
        </div>
      </div>
    </section>
  );
}
